import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../images/navLogo.png';
import Icon2 from '../images/navLogo2.png';
import Flag from '../images/uk-flag.png';
import NotificationBell from './NotificationBell';
import { useAuth } from '../context/AuthContext';
import { useFlow } from '../context/FlowContext';
import React, { useState, useRef, useEffect } from 'react';

import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import ukFlag from '../images/uk-flag.png'; // Assuming this is for 'en'
import itFlag from '../images/italy.png'; // Create this image
import frFlag from '../images/france.png'; // Create this image
import deFlag from '../images/germany.png'; // Create this image
import styles from './NavBar.module.css';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, loadingAuth, logout } = useAuth();
  const { currentFlow } = useFlow();

  const { t, i18n } = useTranslation();


  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const hamburgerButtonRef = useRef(null);

  const languages = [
    { code: 'en', name: t('language_en'), flag: ukFlag },
    { code: 'it', name: t('language_it'), flag: itFlag },
    { code: 'fr', name: t('language_fr'), flag: frFlag },
    { code: 'de', name: t('language_de'), flag: deFlag },
  ];

  // Normalize various language formats to i18n codes
  const normalizeLanguageCode = (lng) => {
    if (!lng) return 'en';
    const lower = String(lng).toLowerCase();
    if (lower.startsWith('en')) return 'en';
    if (lower.startsWith('de')) return 'de';
    if (lower.startsWith('fr')) return 'fr';
    if (lower.startsWith('it')) return 'it';
    // Handle uppercase like "EN", "DE" etc.
    if (lng === 'EN') return 'en';
    if (lng === 'DE') return 'de';
    if (lng === 'FR') return 'fr';
    if (lng === 'IT') return 'it';
    return 'en';
  };

  const changeLanguage = (lng) => {
    const normalized = normalizeLanguageCode(lng);
    i18n.changeLanguage(normalized);
    try {
      localStorage.setItem('preferredLanguage', normalized);
    } catch (_) {}
    setShowLanguageDropdown(false);
  };

  const currentFlag = languages.find(lang => lang.code === i18n.language)?.flag || ukFlag;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          !event.target.closest(`.${styles.profileButton}`)) {
        setShowDropdown(false);
      }
const safeClass = styles.languageButton.replace(/[!\"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '\\$&');

if (
  languageDropdownRef.current &&
  !languageDropdownRef.current.contains(event.target) &&
  !event.target.closest(`.${safeClass}`)
) {
  setShowLanguageDropdown(false);
}

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // On mount, apply stored preferred language (for logged-out sessions too)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('preferredLanguage');
      if (stored && stored !== i18n.language) {
        i18n.changeLanguage(normalizeLanguageCode(stored));
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user logs in (or auth finishes) and API provides language, apply it
  useEffect(() => {
    if (isAuthenticated && currentUser && currentUser.language) {
      const apiLang = normalizeLanguageCode(currentUser.language);
      if (apiLang !== i18n.language) {
        i18n.changeLanguage(apiLang);
      }
      try {
        localStorage.setItem('preferredLanguage', apiLang);
      } catch (_) {}
    }
  }, [isAuthenticated, currentUser, i18n]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getDisplayName = () => {
    if (!currentUser) {
      return t('profile');
    }
    if (currentUser.name && currentUser.surname) {
      return `${currentUser.name} ${currentUser.surname}`;
    }
    if (currentUser.name) {
      return currentUser.name;
    }
    return t('profile');
  };

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleViewProfile = () => {
    navigate('/ProfilePage');
    setShowDropdown(false);
    setIsMobileMenuOpen(false);
  };

  const handleViewBookmarks = () => {
    navigate('/BookmarksPage');
    setShowDropdown(false);
    setIsMobileMenuOpen(false);
  };

  const renderUtilityButtons = () => (
    <>
      {/* Profile/Login dropdown comes first if authenticated, or login button if not */}
      {isAuthenticated ? (
        <div className={styles.profileDropdownContainer} ref={dropdownRef}>
          <button
            className={styles.profileButton}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            {getDisplayName()}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginLeft: '8px', transition: 'transform 0.3s', transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {showDropdown && (
            <ul className={styles.dropdownMenu}>
              <li className={styles.dropdownItem}>
                <button className={styles.dropdownButton} onClick={handleViewProfile}>
                  {t('profile')}
                </button>
              </li>
              {currentFlow === "demand" && (
                <li className={styles.dropdownItem}>
                  <button className={styles.dropdownButton} onClick={handleViewBookmarks}>
                    {t('bookmark')}
                  </button>
                </li>
              )}
              <li className={styles.dropdownItem}>
                <button className={styles.dropdownButton} onClick={handleLogout}>
                  {t('Logout')}
                </button>
              </li>
            </ul>
          )}
        </div>
      ) : (
        <li className={styles.navItem}> {/* Added li here for consistent styling in renderUtilityButtons if needed elsewhere */}
          <Link to="/auth/login?mode=login" className={styles.roundButton}>
            {t('Login')}
          </Link>
        </li>
      )}

      {/* Notification Bell */}
      {isAuthenticated && <NotificationBell />}

      {/* Language selector comes after profile/login */}
      <div className={styles.languageSelectorContainer} ref={languageDropdownRef}>
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className={styles.languageButton}
        >
          <img src={currentFlag} alt="Current Language Flag" className={styles.flag} />
          <svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="butt"   
  strokeLinejoin="miter" 
  style={{ marginLeft: '8px', transition: 'transform 0.3s', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
>
  <polyline points="6 9 12 15 18 9"></polyline>
</svg>
        </button>
        {showLanguageDropdown && (
          <ul className={styles.languageDropdownMenu}>
            {languages.map((lang) => (
              <li key={lang.code} className={styles.languageDropdownItem}>
                <button
                  onClick={() => {
                    changeLanguage(lang.code);
                  }}
                  className={styles.languageDropdownButton}
                >
                  <img src={lang.flag} alt={`${lang.name} Flag`} className={styles.flagSmall} />
                  {lang.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );

  const renderNavLinks = (isMobile = false) => {
    if (loadingAuth) {
      return (
        <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
          <span style={{ color: '#888' }}>{t('Loading')}...</span>
        </li>
      );
    }

    const commonLinks = (
      <>
        <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
          <Link
            to={isAuthenticated && currentUser && currentUser.userType === "offer" ? "/MyProperties" : "/"}
            style={{ 
              color: (isAuthenticated && currentUser && currentUser.userType === "offer" 
                ? (isActive('/MyProperties') || isActive('/MyProperties/'))
                : isActive('/')) 
                ? 'rgba(41, 177, 133, 1)' 
                : '#000' 
            }}
            className={isMobile ? styles.mobileNavLink : styles.link}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            {isAuthenticated && currentUser && currentUser.userType === "offer" ? t('my-property') : t('HOME')}
          </Link>
        </li>
        {/* About link moved here for placement before utility buttons */}
        
      </>
    );

    if (!isAuthenticated) {
      return (
        <>
          {commonLinks}
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/SearchProperty"
              style={{ color: isActive('/SearchProperty') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('Search_Property')}
            </Link>
          </li>
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/OfferProperty"
              style={{ color: isActive('/OfferProperty') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('offer_property')}
            </Link>
          </li>
          
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
          <Link
            to="/about"
            style={{ color: isActive('/about') ? 'rgba(41, 177, 133, 1)' : '#000' }}
            className={isMobile ? styles.mobileNavLink : styles.link}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            {t('About')}
          </Link>
        </li>
        </>
      );
    }

    if (currentFlow === "offer") {
      return (
        <>
          {commonLinks}
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/OfferProperty"
              style={{ color: isActive('/OfferProperty') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('offer_property')}
            </Link>
          </li>
          {/* <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/MyProperties"
              style={{ color: isActive('/MyProperties') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('my-property')}
            </Link>
          </li> */}
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
          <Link
            to="/about"
            style={{ color: isActive('/about') ? 'rgba(41, 177, 133, 1)' : '#000' }}
            className={isMobile ? styles.mobileNavLink : styles.link}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            {t('About')}
          </Link>
        </li>
        </>
      );
    }
    else if (currentFlow === "demand") {
      return (
        <>
          {commonLinks}
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/SearchProperty"
              style={{ color: isActive('/SearchProperty') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('Search_Property')}
            </Link>
          </li>
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/MySearch"
              style={{ color: isActive('/MySearch') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('my-search')}
            </Link>
          </li>
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
            <Link
              to="/myMatchings"
              style={{ color: isActive('/myMatchings') ? 'rgba(41, 177, 133, 1)' : '#000' }}
              className={isMobile ? styles.mobileNavLink : styles.link}
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              {t('my_matchings')}
            </Link>
          </li>
          <li className={isMobile ? styles.mobileNavItem : styles.navItem}>
          <Link
            to="/about"
            style={{ color: isActive('/about') ? 'rgba(41, 177, 133, 1)' : '#000' }}
            className={isMobile ? styles.mobileNavLink : styles.link}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            {t('About')}
          </Link>
        </li>
        </>
      );
    }
    return null;
  };

  return ( 
    <nav className={styles.nav}>
      <div className={styles.logoContainer}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className={styles.logoWithIcon}>
            <img
              src={Icon}
              alt="App Icon"
              className={styles.icon}
            />
            <div className={styles.logoText}>
              <img
              src={Icon2}
              alt="App Icon"
              className={styles.icon2}
            />
            </div>
          </div>
        </Link>
      </div>

      <div className={styles.hamburgerMenu}>
        <button
          className={styles.hamburgerButton}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={t('Toggle navigation')}
          ref={hamburgerButtonRef}
        >
          <span className={styles.hamburgerLine}></span>
          <span className={styles.hamburgerLine}></span>
          <span className={styles.hamburgerLine}></span>
        </button>
      </div>


      <div className={styles.rightNavSection}>
        <ul className={styles.navList}>
          {renderNavLinks()}
        </ul>
        {renderUtilityButtons()}
      </div>


      {isMobileMenuOpen && (
        <div className={styles.mobileMenuOverlay} ref={mobileMenuRef}>
          <div className={styles.mobileMenuContent}>
            <button
              className={styles.mobileMenuCloseButton}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label={t('Close navigation')}
            >
              &times;
            </button>

            <ul className={styles.mobileNavList}>
              {renderNavLinks(true)}
              <li className={styles.mobileNavItem}>
                {renderUtilityButtons()}
              </li>
            </ul>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;